import { useState, type ReactNode } from "react";
import Button from "@/components/Button";
import { useForm, Controller } from "react-hook-form";
import Select from "@/components/Select/Select";
import TextField from "@/components/TextField/TextField";
import Checkbox from "@/components/Checkbox";
import DetailsCard from "@/components/DetailsCard";
import SectionHeading from "@/components/SectionHeading";
import SubSectionHeading from "@/components/SubSectionHeading";
import { ListItemProps } from "@/components/ListItem/ListItem";
import { JobPostCloseFormInput } from "../close/types";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useCanManageJobPostsBool } from "@/hooks/useCapabilities";
import { CloseHireDetailsStep } from "../close/steps/CloseHireDetailsStep";
import { employerOutcomeSupportingText, yesNoNotSetOptions } from "../types";
import { useJobPostCloseForm } from "../hooks/useJobPostCloseForm";
import { toDateTimeFormat } from "@/lib/dateHelpers";
import { DateTime } from "luxon";
import { useJobPostContext } from "../useJobPostContext";

function DetailsColumn({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-y-10">{children}</div>;
}

interface ViewJobOutcomeProps {
  jobPost: any;
  refetch: () => void;
}

export default function ViewJobOutcome({ jobPost, refetch }: ViewJobOutcomeProps) {
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const canManageJobPosts = useCanManageJobPostsBool();
  const { isJobClosed } = useJobPostContext();
  const { handleSubmit: handleJobPostSubmit, isSubmitting } = useJobPostCloseForm(jobPost.id);
  const form = useForm<JobPostCloseFormInput>({
    defaultValues: {
      reason: "Filled",
      closeDate: DateTime.now().toFormat("yyyy-MM-dd"),
      hiringStatus: jobPost?.status || "Incomplete",
      employerData: null,
      careerConsultantVisited: jobPost?.consultantVisitedFacility,
      isPgaFacilityConversion: jobPost?.isFacilityConversion,
      isPgaJobPlacementConversion: jobPost?.isPlacementConversion,
      hireIsPgaPro: jobPost?.isHireePgaPro,
      isNetNewPosition: jobPost?.isNetNewPosition || "Not Set",
      hireCompensationAmount: jobPost?.hiredCompensationAmount
        ? jobPost.hiredCompensationAmount.toFixed(2).toString()
        : null, // Modification needed to account for currency translation. Value is not in cents.
      hireCompensationSchedule: jobPost?.hiredCompensationSchedule || "none",
      previousCompensation: jobPost?.previousCompensation || null,
      sendEmployerSurvey: jobPost?.sendEmployerSurvey !== false,
      hireeId: jobPost?.hiree?.id || "",
      employeeData: jobPost?.hiree || null,
      notes: jobPost?.closeComments || ""
    }
  });

  const clearEditingSection = () => setEditingSection(null);

  const handleSave = async (isHireEdit: boolean) => {
    try {
      const formData = form.getValues();

      // Base update object
      const partialUpdate: Record<string, any> = {
        hiringStatus: formData.hiringStatus,
        sendEmployerSurvey: formData.sendEmployerSurvey
      };

      // Add hire details if editing hire section
      if (isHireEdit) {
        partialUpdate.hireeId = formData.hireeId;
        partialUpdate.employeeData = formData.employeeData;
        partialUpdate.hireIsPgaPro = formData.hireIsPgaPro;
        partialUpdate.hireCompensationAmount = formData.hireCompensationAmount;
        partialUpdate.hireCompensationSchedule = formData.hireCompensationSchedule;
        partialUpdate.previousCompensation = formData.previousCompensation;
      } else {
        // Add employer details if editing employer section
        partialUpdate.careerConsultantVisited = formData.careerConsultantVisited;
        partialUpdate.isNetNewPosition = formData.isNetNewPosition;
        partialUpdate.isPgaFacilityConversion = formData.isPgaFacilityConversion;
        partialUpdate.isPgaJobPlacementConversion = formData.isPgaJobPlacementConversion;
        partialUpdate.notes = formData.notes;
      }

      const result = await handleJobPostSubmit(partialUpdate);
      if (result.data?.updateCloseJob?.success) {
        showSnackbar(`${isHireEdit ? "Hire" : "Employer"} details updated successfully`);
        clearEditingSection();
        refetch();
      } else {
        showSnackbar(
          result.data?.updateCloseJob?.message || `Failed to update ${isHireEdit ? "hire" : "employer"} details`
        );
      }
    } catch (error) {
      showSnackbar(`An error occurred while updating ${isHireEdit ? "hire" : "employer"} details`);
    }
  };

  const handleHireDetailsSave = () => {
    handleSave(true);
  };

  const handleEmployerDetailsSave = () => {
    handleSave(false);
  };

  // Helper to get formatted compensation value
  const getFormattedCompensation = (value: number | null | undefined) => {
    if (!value) return "--";

    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
  };

  // Helper to format Yes/No/Not Sure values
  const formatYesNoNotSet = (value: string | null | undefined) => {
    return value || "--";
  };

  const getHireeName = () => {
    if (!jobPost?.hiree) return "--";

    return `${jobPost.hiree.firstName} ${jobPost.hiree.lastName}`.trim() || "--";
  };

  const getHireDetailsOptions = (): ListItemProps[] => {
    const hireeName = getHireeName();
    const compensationValue = jobPost?.hiredCompensationAmount
      ? (() => {
          const formattedAmount = getFormattedCompensation(jobPost.hiredCompensationAmount);
          return jobPost.hiredCompensationSchedule && jobPost.hiredCompensationSchedule !== "none"
            ? `${formattedAmount} / ${jobPost.hiredCompensationSchedule}`
            : formattedAmount;
        })()
      : "--";

    return [
      {
        iconName: "business_center",
        overline: "Job Status",
        headline: jobPost?.status || "--"
      },
      {
        iconName: "today",
        overline: "Closed On",
        headline: jobPost?.closedDate ? toDateTimeFormat(jobPost.closedDate.toString()) : "--"
      },
      {
        iconName: "person",
        overline: "Hiree",
        headline: hireeName,
        ...(jobPost?.hiree?.pgaId &&
          hireeName !== "--" && {
            action: () =>
              window.open(`/people/${jobPost.hiree.pgaId}/person/overview`, "_blank", "noopener,noreferrer"),
            actionIcon: "open_in_new"
          })
      },
      {
        iconName: "person",
        overline: "Hire is PGA Pro",
        headline: formatYesNoNotSet(jobPost?.isHireePgaPro)
      },
      {
        iconName: "attach_money",
        overline: "Previous Compensation",
        headline: getFormattedCompensation(jobPost?.previousCompensation)
      },
      {
        iconName: "attach_money",
        overline: "New Hire Total Compensation",
        headline: compensationValue
      }
    ];
  };

  const getEmployerDetailsOptions = (): ListItemProps[] => {
    return [
      {
        iconName: "location_on",
        overline: "Facility Visited",
        headline: formatYesNoNotSet(jobPost?.consultantVisitedFacility)
      },
      {
        iconName: "work",
        overline: "New Position at Facility",
        headline: formatYesNoNotSet(jobPost?.isNetNewPosition)
      },
      {
        iconName: "location_on",
        overline: "Facility Conversion",
        headline: formatYesNoNotSet(jobPost?.isFacilityConversion)
      },
      {
        iconName: "person",
        overline: "PGA Placement Conversion",
        headline: formatYesNoNotSet(jobPost?.isPlacementConversion)
      },
      {
        iconName: "3p",
        overline: "Employer Feedback Survey Sent",
        headline: jobPost?.sendEmployerSurvey !== false ? "Yes" : "No"
      },
      {
        iconName: "sticky_note_2",
        overline: "Closing Comments",
        headline: jobPost?.closeComments || "--",
        longContent: !!jobPost?.closeComments
      }
    ];
  };
  if (!canManageJobPosts && !isJobClosed(jobPost)) return null;

  const renderHireDetailsEdit = () => (
    <div className="space-y-6">
      <CloseHireDetailsStep form={form} />
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="text" onClick={clearEditingSection} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="filled" onClick={handleHireDetailsSave} disabled={isSubmitting}>
          Save
        </Button>
      </div>
    </div>
  );

  const renderEmployerDetailsEdit = () => (
    <div className="space-y-6">
      <Controller
        name="careerConsultantVisited"
        control={form.control}
        render={({ field }) => (
          <Select
            label="Facility Visited"
            options={yesNoNotSetOptions}
            onSelect={(value: string) => {
              field.onChange(value);
            }}
            value={field.value}
            supportingText={employerOutcomeSupportingText.facilityVisited}
          />
        )}
      />
      <Controller
        name="isNetNewPosition"
        control={form.control}
        render={({ field }) => (
          <Select
            label="New Position at Facility"
            options={yesNoNotSetOptions}
            onSelect={(value: string) => {
              field.onChange(value);
            }}
            value={field.value}
            supportingText={employerOutcomeSupportingText.newPositionAtFacility}
          />
        )}
      />
      <Controller
        name="isPgaFacilityConversion"
        control={form.control}
        render={({ field }) => (
          <Select
            label="Facility Conversion"
            options={yesNoNotSetOptions}
            onSelect={(value: string) => {
              field.onChange(value);
            }}
            value={field.value}
            supportingText={employerOutcomeSupportingText.facilityConversion}
          />
        )}
      />
      <Controller
        name="isPgaJobPlacementConversion"
        control={form.control}
        render={({ field }) => (
          <Select
            label="PGA Placement Conversion"
            options={yesNoNotSetOptions}
            onSelect={(value: string) => {
              field.onChange(value);
            }}
            value={field.value}
            supportingText={employerOutcomeSupportingText.pgaPlacementConversion}
          />
        )}
      />
      <div className="space-y-4 pt-4">
        <SubSectionHeading>Employer Feedback</SubSectionHeading>
        <Controller
          name="sendEmployerSurvey"
          control={form.control}
          render={({ field: { onChange, value } }) => (
            <Checkbox
              name="sendEmployerSurvey"
              label="Send Employer Satisfaction Survey Email"
              checked={value}
              onChange={onChange}
            />
          )}
        />
      </div>
      <div className="pt-4">
        <SubSectionHeading>Job Post Closing Comments</SubSectionHeading>
        <p className="pt-2 pb-6 text-body-medium">
          Provide any additional context that should be kept with this job post.
        </p>
        <Controller
          name="notes"
          control={form.control}
          render={({ field }) => (
            <TextField
              type="textarea"
              rows={6}
              label="Closing Comments"
              value={field.value || ""}
              onChangeText={field.onChange}
              error={!!form.formState.errors.notes}
              errorText={form.formState.errors.notes?.message}
              className="w-full"
            />
          )}
        />
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="text" onClick={clearEditingSection} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="filled" onClick={handleEmployerDetailsSave} disabled={isSubmitting}>
          Save
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col">
      <SectionHeading
        heading="Job Post Outcome"
        body="View and manage hire details and employer information for this closed job post."
      />
      <div className="flex flex-col md:flex-row gap-x-10">
        <div id="contact-details-column-1" className="basis-1/3 pb-10">
          <DetailsColumn>
            <DetailsCard
              heading="Hire Details"
              options={getHireDetailsOptions()}
              editingSection={editingSection}
              onEdit={canManageJobPosts ? () => setEditingSection("Hire Details") : undefined}
              renderEdit={canManageJobPosts ? renderHireDetailsEdit : undefined}
            />
          </DetailsColumn>
        </div>
        <div id="contact-details-column-2" className="basis-2/3">
          <DetailsColumn>
            <DetailsCard
              heading="Employer Details"
              options={getEmployerDetailsOptions()}
              editingSection={editingSection}
              onEdit={canManageJobPosts ? () => setEditingSection("Employer Details") : undefined}
              renderEdit={canManageJobPosts ? renderEmployerDetailsEdit : undefined}
            />
          </DetailsColumn>
        </div>
      </div>
    </div>
  );
}
